<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <img src="https://www.dof.gov.ph/wp-content/uploads/2024/02/DOF.png" alt="DOF Logo" class="login-logo">
          <h1>Legislative Monitoring Portal</h1>
          <p>Department of Finance</p>
        </div>
        
        <div class="login-form-container">
          <h2>Sign In</h2>
          <form @submit.prevent="handleLogin" class="login-form">
            <div class="form-group">
              <label for="email">Email Address</label>
              <InputText 
                id="email" 
                v-model="email" 
                type="email" 
                placeholder="Enter your email"
                :class="{ 'p-invalid': errors.email }"
                required
              />
              <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
            </div>
            
            <div class="form-group">
              <label for="password">Password</label>
              <Password 
                id="password"
                v-model="password" 
                placeholder="Enter your password"
                :feedback="false"
                toggleMask
                :class="{ 'p-invalid': errors.password }"
                required
              />
              <small v-if="errors.password" class="p-error">{{ errors.password }}</small>
            </div>
            
            <div v-if="errors.general" class="error-message">
              <small class="p-error">{{ errors.general }}</small>
            </div>
            
            <div class="form-options">
              <div class="remember-me">
                <Checkbox v-model="rememberMe" inputId="remember" binary />
                <label for="remember">Remember me</label>
              </div>
              <a href="#" class="forgot-password">Forgot Password?</a>
            </div>
            
            <Button 
              type="submit" 
              label="Sign In" 
              class="login-button"
              :loading="loading"
              icon="pi pi-sign-in"
            />
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { authStore } from '../store/auth.js'
import api from '../axios.js'

export default {
  components: {
    InputText,
    Password,
    Button,
    Checkbox
  },
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      loading: false,
      errors: {}
    }
  },
  methods: {
    async handleLogin() {
      this.loading = true
      this.errors = {}
      
      if (!this.email) {
        this.errors.email = 'Email is required'
      }
      if (!this.password) {
        this.errors.password = 'Password is required'
      }
      
      if (Object.keys(this.errors).length > 0) {
        this.loading = false
        return
      }
      
      try {
        const response = await api.post('/login', {
          email: this.email,
          password: this.password
        })
        
        authStore.login(response.data.token, response.data.user)
        this.$router.push('/')
      } catch (error) {
        console.log(error);
        if (error.response?.data?.error) {
          this.errors.general = error.response.data.error
        } else {
          this.errors.general = 'Login failed'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-container {
  width: 100%;
  max-width: 400px;
}

.login-card {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 20px 40px rgba(30, 58, 138, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo {
  height: 60px;
  margin-bottom: 1rem;
}

.login-header h1 {
  color: #1e3a8a;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.login-header p {
  color: #64748b;
  font-size: 0.9rem;
}

.login-form-container {
  padding-top: 0;
  margin-top: 0;
}

.login-form-container h2 {
  color: #1e3a8a;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #1e3a8a;
  font-size: 0.9rem;
}

:deep(.p-inputtext),
:deep(.p-password input) {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

:deep(.p-inputtext:focus),
:deep(.p-password input:focus) {
  border-color: #1e3a8a;
  box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
}

:deep(.p-invalid) {
  border-color: #ef4444 !important;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.remember-me label {
  color: #64748b;
  font-weight: 500;
}

.forgot-password {
  color: #1e3a8a;
  text-decoration: none;
  font-weight: 500;
}

.forgot-password:hover {
  text-decoration: underline;
}

.login-button {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
  border: none !important;
  padding: 0.75rem !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  transition: all 0.3s !important;
}

.login-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3) !important;
}

.p-error {
  color: #ef4444;
  font-size: 0.8rem;
}

.error-message {
  text-align: center;
  padding: 0.5rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}

@media (max-width: 480px) {
  .login-card {
    padding: 2rem;
  }
  
  .login-header h1 {
    font-size: 1.3rem;
  }
}
</style>